import React, {useEffect, useState, useRef} from 'react';
import {connect} from 'react-redux';
import {RouterSelfInstallSteps} from './Assets/variables';
import {setStep, resetState, setError, initiateWizard, getWizardStep} from './RouterSelfInstallSlice';
import './Assets/RouterSelfInstallBlock.scss';
import IntroScreen from './Screens/IntroScreen';
import NetConfigScreen from './Screens/NetconfigScreen';
import SelfCheckScreen from './Screens/SelfcheckScreen';
import SuccessScreen from './Screens/SuccessScreen';
import ErrorScreen from './Screens/ErrorScreen';
import LoadingScreen from './Screens/LoadingScreen';
import {resetLoginData} from 'app/utilities/authentication/authenticationSlice';

const RouterSelfInstallBlockMain = (props) => {
  const [intervalId, setIntervalId] = useState(null);
  const [reqId, setReqId] = useState(null);
  const isVisible = useRef(true);
  const lastCallTime = useRef(Date.now());

  // Handle page visibility changes for all platforms
  useEffect(() => {
    const handleVisibilityChange = () => {
      const wasVisible = isVisible.current;
      isVisible.current = !document.hidden;

      if (document.hidden) {
        // App went to background - clear interval to prevent issues
        console.log('App hidden - pausing polling');
        if (intervalId) {
          clearInterval(intervalId);
          setIntervalId(null);
        }
      } else if (!wasVisible && isVisible.current) {
        // App returned to foreground - resume polling if needed
        console.log('App visible - resuming polling');

        if (props.step === RouterSelfInstallSteps.LOADING && reqId) {
          // Check if enough time has passed to avoid rapid calls
          const now = Date.now();
          const timeSinceLastCall = now - lastCallTime.current;

          // If more than 15 seconds passed, call immediately
          if (timeSinceLastCall >= 15000) {
            lastCallTime.current = now;
            props.getWizardStep({requestId: reqId});
          }

          // Always restart the interval if we don't have one
          if (!intervalId) {
            const newIntervalId = setInterval(() => {
              lastCallTime.current = Date.now();
              props.getWizardStep({requestId: reqId});
            }, 20000);
            setIntervalId(newIntervalId);
          }
        }
      }
    };

    // Check if visibility API is supported (it is on all modern browsers)
    if (typeof document.hidden !== 'undefined') {
      document.addEventListener('visibilitychange', handleVisibilityChange);

      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }
  }, [intervalId, props.step, reqId, props.getWizardStep]);

  useEffect(() => {
    return () => {
      try {
        clearInterval(intervalId);
      } catch {}
      setIntervalId(null);
    };
  }, []);

  useEffect(() => {
    if (props.step != RouterSelfInstallSteps.LOADING) {
      try {
        clearInterval(intervalId);
      } catch {}
      setIntervalId(null);
    }
  }, [props.step]);

  useEffect(() => {
    // Only execute step updates when page is visible
    if (!isVisible.current) {
      console.log('Page not visible - skipping step update');
      return;
    }

    if (props.shouldUpdateStep && ((props.error.isError && props.step !== RouterSelfInstallSteps.ERROR) || props.stepUpdateTo)) {
      const stepToUpdate = props.error.isError ? RouterSelfInstallSteps.ERROR : props.stepUpdateTo;

      if (stepToUpdate && stepToUpdate !== '') {
        console.log('Setting timeout for step:', stepToUpdate);

        const executeStepUpdate = () => {
          // Double-check visibility before executing
          if (isVisible.current) {
            console.log('Executing step update to:', stepToUpdate);
            props.setStep(stepToUpdate);
          } else {
            console.log('Page hidden - canceling step update');
          }
        };

        const stepTimeout = setTimeout(executeStepUpdate, 3000);

        return () => {
          clearTimeout(stepTimeout);
        };
      }
    }
  }, [props.error.isError, props.step, props.shouldUpdateStep, props.stepUpdateTo]);

  const startNiud = async (wifiName, wifiPass) => {
    const retVal = await props.initiateWizard({wifiUser: wifiName, wifiPass: wifiPass});
    if (retVal.payload.data.requestId) {
      setReqId(retVal.payload.data.requestId);
      setIntervalId(
        setInterval(() => {
          props.getWizardStep({requestId: retVal.payload.data.requestId});
        }, 20000),
      );
    }
  };

  const renderStep = () => {
    switch (props.step) {
      case RouterSelfInstallSteps.INTRO:
        return <IntroScreen {...props} />;
      case RouterSelfInstallSteps.LOADING:
        return <LoadingScreen {...props} />;
      case RouterSelfInstallSteps.NETCONFIG:
        return <NetConfigScreen proceedConfig={startNiud} {...props} />;
      case RouterSelfInstallSteps.SELFCHECK:
        return <SelfCheckScreen {...props} reqId={reqId} />;
      case RouterSelfInstallSteps.SUCCESS:
        return <SuccessScreen {...props} />;
      case RouterSelfInstallSteps.ERROR:
        return <ErrorScreen {...props} />;
      default:
        return null;
    }
  };

  return <div className='RouterSelfInstallBlock'>{renderStep()}</div>;
};

const mapStateToProps = (state) => {
  return {...state.routerSelfInstall};
};

const mapDispatchToProps = {
  setStep,
  resetState,
  setError,
  initiateWizard,
  getWizardStep,
  resetLoginData,
};
export default connect(mapStateToProps, mapDispatchToProps)(RouterSelfInstallBlockMain);
